// @flow

import * as React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import type { State } from "@store/reducers";
import type { ActiveReferralsTrackerItem } from "@modules/reporting/reducer/referral-tracker/types";
import type { PaginationExtendedType } from "@types";
import { formatDateTime } from "@transformers/date";
import actionCreators from "@modules/reporting/actionCreator";
import {
  selectActiveReferrals,
  selectActiveReferralsPagination,
  selectActiveReferralsStats,
} from "@modules/reporting/selectors/referral-tracker";
import { GSGrid, Pagination, StatTileGroup, View } from "@components";
import { SUPPLIER_REFERRAL_REPORT_TYPE } from "@modules/reporting/constants/reports";
import ReferralRTWModal from "@modules/reporting/components/referral-tracker/referral-rtw-modal";
import ReferralLogModal from "@modules/reporting/components/referral-tracker/referral-log-modal";
import ReferralStatusModal from "@modules/reporting/components/referral-tracker/referral-status-modal";
import { selectUserType } from "@modules/account/selectors";
import { USER_TYPES } from "@modules/account/constants";
import "../styles.scss";
import ReferralTrackerFilter from "@modules/reporting/components/referral-tracker/referral-tracker-filter";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  stats: Object,
  activeReferrals: ActiveReferralsTrackerItem[],
  pagination: PaginationExtendedType,
  selectUserType: string,
}>;

type PropsFromDispatch = $Exact<{
  getActiveReferrals: typeof actionCreators.GetSupplierActiveReferrals,
  getSupplierReferralDetails: typeof actionCreators.GetSupplierReferralDetails,
  getRTWReferralDetails: typeof actionCreators.GetRTWReferralDetails,
  getLogReferralDetails: typeof actionCreators.GetLogReferralDetails,
  setReferralCommonData: typeof actionCreators.SetReferralCommonData,
  toggleStatusReferralDetailsModal: typeof actionCreators.ToggleStatusReferralDetailsModal,
  getClosedReferralsStatuses: typeof actionCreators.GetSupplierClosedReferralsStatuses,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

type OwnState = $Exact<{
  filters: null | Object,
}>;

export class ActiveReferralsSection extends React.Component<Props, OwnState> {
  state = {
    filters: null,
  };

  componentDidMount() {
    this.props.getActiveReferrals({ clientId: this.props.clientId, page: 1 });
    this.props.getClosedReferralsStatuses({ clientId: this.props.clientId });
  }

  handlePaginationChange = (page: number) => {
    this.props.getActiveReferrals({
      clientId: this.props.clientId,
      page,
      filters: this.state.filters,
    });
  };

  get statsItems() {
    return [
      {
        title: this.props.t("CURRENT_ACTIVE_REFERRALS"),
        value: <div>{this.props.stats.currentActiveReferrals}</div>,
        className: "bg-light-grey",
      },
      {
        title: this.props.t("REFERRALS_OLD_THEN", { num: "7" }),
        value: <div>{this.props.stats.referralsLessThanSevenDaysOld}</div>,
      },
      {
        title: this.props.t("REFERRALS_OLD_THEN", { num: "30" }),
        value: <div>{this.props.stats.referralsLessThanThirtyDaysOld}</div>,
      },
      {
        title: this.props.t("REFERRALS_OLD_THEN", { num: "90" }),
        value: <div>{this.props.stats.referralsLessThanNinetyDaysOld}</div>,
      },
      {
        title: this.props.t("REFERRALS_OLDER_THEN_90"),
        value: <div>{this.props.stats.referralsOlderThenNinetyDays}</div>,
        className: "overdue",
      },
    ];
  }

  getSupplierReferralDetails = (row: *) => () => {
    this.props.getSupplierReferralDetails({
      supplierReferralId: row.referralId,
      clientId: this.props.clientId,
      referralType: SUPPLIER_REFERRAL_REPORT_TYPE.ACTIVE_REFERRALS,
    });
  };

  handleStatusCellClick = (row: *) => () => {
    this.props.setReferralCommonData({ ...row, clientId: this.props.clientId });
    this.props.toggleStatusReferralDetailsModal();
  };

  handleLogCellClick = (row: *) => () => {
    this.props.setReferralCommonData({ ...row, clientId: this.props.clientId });
    this.props.getLogReferralDetails({
      supplierReferralId: row.referralId,
      clientId: this.props.clientId,
    });
  };

  handleRTWCellClick = (row: *) => () => {
    if (row.referralType !== "MANUAL_REFERRAL") {
      this.props.setReferralCommonData({
        ...row,
        clientId: this.props.clientId,
        supplierReferralId: row.referralId,
      });
      this.props.getRTWReferralDetails({
        supplierReferralId: row.referralId,
        clientId: this.props.clientId,
      });
    }
  };

  get columns() {
    let res = [
      {
        name: this.props.t("REFERRAL_ID"),
        width: 90,
        frozen: "left",
        key: "referralId",
      },
      {
        name: this.props.t("EMPLOYEE_REF"),
        align: "left",
        key: "employeeRef",
        tooltipField: "employeeRef",
      },
      {
        name: this.props.t("FIRST_NAME"),
        width: 150,
        align: "left",
        key: "firstName",
      },
      {
        name: this.props.t("LAST_NAME"),
        width: 150,
        align: "left",
        key: "lastName",
      },
      {
        name: this.props.t("DEPARTMENT"),
        width: 212,
        align: "left",
        key: "departmentNames",
        renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.departmentNames),
      },
      {
        name: this.props.t("REFERRAL_TYPE"),
        width: 150,
        renderCell: ({ row }: *) => {
          return row.referralType ? this.props.t(row.referralType) : "";
        },
      },
      {
        name: this.props.t("SUPPLIER_NAME"),
        width: 150,
        align: "left",
        key: "supplierName",
      },
      {
        name: this.props.t("DATE_REFERRED"),
        width: 150,
        renderCell: ({ row }: *) => {
          return row.dateReferred ? formatDateTime(row.dateReferred) : "";
        },
      },
      {
        name: this.props.t("REASON_FOR_REFERRAL"),
        width: 212,
        key: "reason",
        tooltipField: "reason",
        align: "left",
        renderCell: ({ row }: *) => GSGrid.render.ellipsis(row.reason),
      },
      {
        name: this.props.t("DETAILS"),
        width: 80,
        frozen: "right",
        align: "center",
        renderCell: ({ row }: *) => {
          return (
            <span onClick={this.getSupplierReferralDetails(row)} className="material-icons pointer">
              search
            </span>
          );
        },
      },
    ];
    if (this.props.selectUserType !== USER_TYPES.FULL_HR_ACCESS) {
      res.push({
        name: this.props.t("RTW"),
        width: 60,
        frozen: "right",
        align: "center",
        renderCell: ({ row }: *) => {
          return (
            <span
              onClick={row.referralType !== "MANUAL_REFERRAL" && this.handleRTWCellClick(row)}
              className={`material-icons ${
                row.referralType === "MANUAL_REFERRAL"
                  ? "cursor-disabled text-color-disabled-light"
                  : "cursor-pointer"
              }`}
            >
              edit
            </span>
          );
        },
      });
      res.push({
        name: this.props.t("LOG"),
        width: 60,
        frozen: "right",
        align: "center",
        renderCell: ({ row }: *) => {
          return (
            <span onClick={this.handleLogCellClick(row)} className="material-icons pointer">
              edit
            </span>
          );
        },
      });
      res.push({
        name: this.props.t("STATUS"),
        width: 80,
        frozen: "right",
        align: "center",
        renderCell: ({ row }: *) => {
          return (
            <span onClick={this.handleStatusCellClick(row)} className="material-icons pointer">
              edit
            </span>
          );
        },
      });
    }

    return res;
  }

  handleSubmitFilter = () => {
    this.props.getActiveReferrals({
      clientId: this.props.clientId,
      page: 1,
      filters: this.state.filters,
    });
  };

  handleChangeFilter = (filters: Object) => {
    this.setState({ filters });
  };

  render() {
    const { activeReferrals, pagination } = this.props;
    return (
      <React.Fragment>
        <StatTileGroup data={this.statsItems} />

        <View className="gs-container">
          <ReferralTrackerFilter
            clientId={this.props.clientId}
            handleSubmit={this.handleSubmitFilter}
            handleChange={this.handleChangeFilter}
            data={this.state.filters}
          />
          <GSGrid columns={this.columns} rows={activeReferrals} />
          <Pagination
            className="my-3 d-flex flex-fill justify-content-center"
            currentPage={pagination.pageNumber}
            totalPages={pagination.totalPages}
            onChange={this.handlePaginationChange}
          />
        </View>
        <ReferralRTWModal />
        <ReferralLogModal clientId={this.props.clientId} />
        <ReferralStatusModal clientId={this.props.clientId} />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    stats: selectActiveReferralsStats(state),
    activeReferrals: selectActiveReferrals(state),
    pagination: selectActiveReferralsPagination(state),
    selectUserType: selectUserType(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getActiveReferrals: actionCreators.GetSupplierActiveReferrals,
  getSupplierReferralDetails: actionCreators.GetSupplierReferralDetails,
  getRTWReferralDetails: actionCreators.GetRTWReferralDetails,
  getLogReferralDetails: actionCreators.GetLogReferralDetails,
  setReferralCommonData: actionCreators.SetReferralCommonData,
  toggleStatusReferralDetailsModal: actionCreators.ToggleStatusReferralDetailsModal,
  getClosedReferralsStatuses: actionCreators.GetSupplierClosedReferralsStatuses,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ActiveReferralsSection));
